import { useMutation, useQuery } from '@tanstack/react-query';
import Config from '@src/utils/api/config';
import { useSubscriptionContext } from './provider';
import { downloadExcel } from '@src/utils/download-helper';
import moment from 'moment';
import { allSubscriptionsAPI, exportSubscriptionsAPI, SubscriptionItem, subscriptionOverview, subscriptionPurchaseCounts } from '@src/api/subscription';

export const useSubscriptionService = () => {
  const { state, handlePageChange } = useSubscriptionContext();
  const { page, rowsPerPage, debouncedSearch, fromdate, todate, status, planType } = state;
  const newPage = (page || 0) + 1;

  const PLAN_SUBSCRIPTION_ID: Record<string, string> = {
    'WEEKLY': 'gwz_sub_1_1w',
    'MONTHLY': 'gwz_sub_2_1m',
  };
  const subscriptionId = planType ? PLAN_SUBSCRIPTION_ID[planType] : undefined;

  const queryParams = {
    page: newPage,
    size: rowsPerPage,
    partnerId: Config.PARTNER_ID,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(fromdate ? { startDate: fromdate } : {}),
    ...(todate ? { endDate: todate } : {}),
    ...(status ? { status } : { status }),
    ...(subscriptionId ? { subscriptionId } : {}),
  };

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['subscriptions', queryParams],
    queryFn: () => allSubscriptionsAPI(queryParams),
  });

  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['subscription-overview', { fromdate, todate, partnerId: Config.PARTNER_ID }],
    queryFn: () => subscriptionOverview({
      ...(fromdate ? { startDate: fromdate } : {}),
      ...(todate ? { endDate: todate } : {}),
    }),
  });

  const { data: counts, refetch: refetchCounts } = useQuery({
    queryKey: ['subscription-purchase-counts'],
    queryFn: () => subscriptionPurchaseCounts(),
  });

  const onExportSubscriptions = useMutation({
    mutationKey: ['export-subscriptions'],
    mutationFn: async () => {
      const res = await exportSubscriptionsAPI({
        partnerId: Config.PARTNER_ID,
        ...(fromdate ? { startDate: fromdate } : {}),
        ...(todate ? { endDate: todate } : {}),
        ...(status ? { status } : {}),
        ...(planType ? { planType } : {}),
      });

      const rows = (res?.items || res || []).map((item: SubscriptionItem) => ({
        'User ID': item.user?.id,
        'Display Name': item.user?.displayName,
        'Plan': item.subscription.name,
        'Amount': item.subscription.price,
        'Status': item.status,
        'Transaction Date': item.startedAt ? moment.parseZone(item.startedAt).format('MM-DD-YYYY hh:mm A') : '-',
        'Expires On': item.expiresAt ? moment.parseZone(item.expiresAt).format('MM-DD-YYYY') : '-',
      }));

      downloadExcel(rows, 'subscriptions-export');
    },
  });

  const onRefresh = () => {
    handlePageChange(null, 0);
    refetch();
    refetchStats();
    refetchCounts();
  };

  return {
    data,
    subscriptions: data?.items || [],
    totalRecords: data?.totalRecords ?? 0,
    stats,
    counts,
    isLoading,
    isRefreshing: isRefetching,
    onRefresh,
    onExportSubscriptions,
  };
};
